import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { inferenceAPI, automationAPI } from '../../lib/api';
import {
  Power, PlayCircle, StopCircle, Loader2, Plus, Trash2, RefreshCw,
  Clock, AlertTriangle, CheckCircle2, XCircle, Eye, EyeOff, RotateCcw,
  ExternalLink,
} from 'lucide-react';

const inputCls = 'w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500';

const fmtUK = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    timeZone: 'Europe/London',
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

const fmtTime = (value) => (value ? String(value).slice(0, 5) : '—');


function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-xs text-slate-400 mt-1.5">{hint}</p>}
    </div>
  );
}

function Toggle({ checked, onChange, label, hint, disabled }) {
  return (
    <div className="flex items-start gap-3">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`mt-0.5 relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-40 ${
          checked ? 'bg-blue-600' : 'bg-slate-300'
        }`}
      >
        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-4.5' : 'translate-x-1'
        }`} style={{ transform: checked ? 'translateX(18px)' : 'translateX(4px)' }} />
      </button>
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-700">{label}</p>
        {hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}
      </div>
    </div>
  );
}

function UkClock() {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const time = now.toLocaleTimeString('en-GB', {
    timeZone: 'Europe/London',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const date = now.toLocaleDateString('en-GB', {
    timeZone: 'Europe/London',
    weekday: 'short', day: 'numeric', month: 'short',
  });

  return (
    <div className="flex items-center gap-2 text-sm">
      <Clock size={14} className="text-slate-400" />
      <span className="text-slate-400">Now in UK</span>
      <span className="font-mono font-semibold text-slate-900 tabular-nums">{time}</span>
      <span className="text-slate-400">{date}</span>
    </div>
  );
}


function Stat({ label, value, tone }) {
  const colour = tone === 'warn' ? 'text-amber-600' : tone === 'good' ? 'text-emerald-600' : 'text-slate-900';
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
      <p className={`text-xl font-bold mt-0.5 ${colour}`}>{value}</p>
    </div>
  );
}


// ─── Instance panel ───

function InstancePanel({ status, onRefresh }) {
  const [busy, setBusy]         = useState(null);
  const [apiKey, setApiKey]     = useState('');
  const [showKey, setShowKey]   = useState(false);
  const [instances, setInstances] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const instance = status?.instance;
  const endpointOk = status?.endpoint?.available;

  const saveKey = async () => {
    if (!apiKey.trim()) return toast.error('Enter the Vast API key first');
    setBusy('key');
    try {
      await inferenceAPI.setVastCredential(apiKey.trim());
      toast.success('API key saved');
      setApiKey('');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the key');
    } finally {
      setBusy(null);
    }
  };

  const loadInstances = async () => {
    setLoadingList(true);
    try {
      const { data } = await inferenceAPI.getInstances();
      setInstances(data.instances || []);
      if (!data.instances?.length) toast.info('No instances on this Vast account');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reach Vast');
    } finally {
      setLoadingList(false);
    }
  };

  const pickInstance = async (id) => {
    setBusy('pick');
    try {
      await inferenceAPI.setInstance(id);
      toast.success(id ? `Instance ${id} selected` : 'Instance cleared');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    } finally {
      setBusy(null);
    }
  };

  const control = async (action) => {
    setBusy(action);
    try {
      if (action === 'start') {
        await inferenceAPI.startInstance();
        toast.success('Start requested — the endpoint takes a few minutes to answer');
      } else {
        const { data } = await inferenceAPI.stopInstance();
        toast.success(`Stopped. ${data.queued || 0} job(s) stay queued for the next window.`);
      }
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Could not ${action} the instance`);
    } finally {
      setBusy(null);
    }
  };

  const waitReady = async () => {
    setBusy('wait');
    try {
      const { data } = await inferenceAPI.waitInstanceReady(300);
      if (data.ready) toast.success('Endpoint is answering');
      else toast.error(data.detail || 'Endpoint did not come up in time');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">GPU instance</h2>
          <p className="text-xs text-slate-400 mt-0.5">
            The box that runs generation. Stopping it never fails queued work.
          </p>
        </div>
        <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-1 rounded border font-semibold ${
          endpointOk
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'bg-slate-100 text-slate-500 border-slate-200'
        }`}>
          {endpointOk ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
          {endpointOk ? 'Endpoint answering' : 'Endpoint offline'}
        </span>
      </div>

      {!status?.vast_configured && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
          <p className="text-xs text-amber-800 mb-2 flex items-start gap-1.5">
            <AlertTriangle size={13} className="shrink-0 mt-0.5" />
            No Vast API key stored. Windows can still open against a box you start by hand,
            but automatic start and stop needs the key.
          </p>
        </div>
      )}

      <Field label="Vast API key"
        hint={status?.credential?.is_set
          ? `Stored as ${status.credential.masked}. Entering a new one replaces it.`
          : 'Encrypted before storage and never readable again. Found under Account in the Vast console.'}>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <input
              type={showKey ? 'text' : 'password'}
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              autoComplete="off"
              placeholder={status?.credential?.is_set ? 'Enter a new key to replace' : 'Vast API key'}
              className={`${inputCls} font-mono text-xs pr-10`}
            />
            <button type="button" onClick={() => setShowKey(s => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
              {showKey ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
          <button onClick={saveKey} disabled={busy === 'key' || !apiKey.trim()}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 shrink-0">
            {busy === 'key' && <Loader2 size={14} className="animate-spin" />} Save key
          </button>
        </div>
      </Field>

      <Field label="Instance"
        hint="Interruptible instances can be reclaimed while stopped, so a window may open onto a missing box.">
        <div className="flex items-center gap-2 flex-wrap">
          <input
            value={status?.instance_id || ''}
            readOnly
            placeholder="No instance selected"
            className={`${inputCls} font-mono text-xs max-w-[220px] bg-slate-50`}
          />
          <button onClick={loadInstances} disabled={loadingList || !status?.vast_configured}
            className="flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
            {loadingList ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            List instances
          </button>
          {status?.instance_id && (
            <button onClick={() => pickInstance(null)} disabled={busy === 'pick'}
              className="px-3 py-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">
              Clear
            </button>
          )}
        </div>
      </Field>

      {instances.length > 0 && (
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-1">
          {instances.map(i => (
            <button key={i.id} onClick={() => pickInstance(String(i.id))}
              className={`w-full text-left flex items-center justify-between gap-3 px-2 py-1.5 rounded text-xs transition-colors ${
                String(i.id) === String(status?.instance_id)
                  ? 'bg-blue-50 text-blue-700'
                  : 'hover:bg-white text-slate-700'
              }`}>
              <span className="font-mono">{i.id}</span>
              <span className="text-slate-500 truncate">
                {i.gpu_name} · {i.actual_status} · {i.is_bid ? 'interruptible' : 'on-demand'}
                {i.dph_total ? ` · $${Number(i.dph_total).toFixed(3)}/hr` : ''}
              </span>
            </button>
          ))}
        </div>
      )}

      {instance && !instance.error && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 border-t border-slate-100">
          <Stat label="State" value={instance.actual_status || '—'}
            tone={instance.actual_status === 'running' ? 'good' : undefined} />
          <Stat label="GPU" value={instance.gpu_name || '—'} />
          <Stat label="Rental" value={instance.is_bid ? 'Interruptible' : 'On-demand'}
            tone={instance.is_bid ? 'warn' : undefined} />
          <Stat label="Cost" value={instance.dph_total ? `$${Number(instance.dph_total).toFixed(3)}/hr` : '—'} />
        </div>
      )}

      {instance?.error && (
        <p className="text-xs text-red-600 flex items-start gap-1.5">
          <XCircle size={13} className="shrink-0 mt-0.5" /> {instance.error}
        </p>
      )}

      <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-slate-100">
        <button onClick={() => control('start')} disabled={busy !== null || !status?.instance_id}
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-40">
          {busy === 'start' ? <Loader2 size={14} className="animate-spin" /> : <Power size={14} />} Start
        </button>
        <button onClick={() => control('stop')} disabled={busy !== null || !status?.instance_id}
          className="flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-40">
          {busy === 'stop' ? <Loader2 size={14} className="animate-spin" /> : <StopCircle size={14} />} Stop
        </button>
        <button onClick={waitReady} disabled={busy !== null}
          className="flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-40">
          {busy === 'wait' ? <Loader2 size={14} className="animate-spin" /> : <Clock size={14} />} Wait until ready
        </button>
      </div>
    </div>
  );
}


// ─── Windows ───

function WindowRow({ window: win, onSave, onDelete, onOpen, isCurrent }) {
  const [form, setForm] = useState({
    name:                  win.name,
    start_time_local:      fmtTime(win.start_time_local),
    max_duration_minutes:  win.max_duration_minutes,
    idle_shutdown_minutes: win.idle_shutdown_minutes,
    is_active:             win.is_active,
  });
  const [saving, setSaving] = useState(false);

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const dirty =
    form.name !== win.name ||
    form.start_time_local !== fmtTime(win.start_time_local) ||
    Number(form.max_duration_minutes) !== win.max_duration_minutes ||
    Number(form.idle_shutdown_minutes) !== win.idle_shutdown_minutes ||
    form.is_active !== win.is_active;

  const save = async () => {
    setSaving(true);
    try {
      await onSave(win.id, {
        name:                  form.name.trim(),
        start_time_local:      form.start_time_local,
        max_duration_minutes:  parseInt(form.max_duration_minutes, 10),
        idle_shutdown_minutes: parseInt(form.idle_shutdown_minutes, 10),
        is_active:             form.is_active,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`bg-white border rounded-xl p-4 shadow-sm ${isCurrent ? 'border-emerald-300' : 'border-slate-200'}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <input value={form.name} onChange={e => update('name', e.target.value)}
          className={`${inputCls} max-w-[140px]`} />
        <input type="time" value={form.start_time_local}
          onChange={e => update('start_time_local', e.target.value)}
          className={`${inputCls} max-w-[120px]`} />
        <div className="flex items-center gap-1.5">
          <input type="number" min="10" value={form.max_duration_minutes}
            onChange={e => update('max_duration_minutes', e.target.value)}
            className={`${inputCls} max-w-[80px]`} />
          <span className="text-xs text-slate-400">max run </span>
        </div>
        <div className="flex items-center gap-1.5">
          <input type="number" min="1" value={form.idle_shutdown_minutes}
            onChange={e => update('idle_shutdown_minutes', e.target.value)}
            className={`${inputCls} max-w-[70px]`} />
          <span className="text-xs text-slate-400">min idle</span>
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={form.is_active}
            onChange={e => update('is_active', e.target.checked)} className="h-4 w-4 accent-blue-600" />
          Active
        </label>

        <div className="ml-auto flex items-center gap-1">
          {dirty && (
            <button onClick={save} disabled={saving}
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50">
              {saving && <Loader2 size={12} className="animate-spin" />} Save
            </button>
          )}
          {!isCurrent && (
            <button onClick={() => onOpen(win.id)} title="Open this window now"
              className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors">
              <PlayCircle size={15} />
            </button>
          )}
          <button onClick={() => onDelete(win)} title="Remove window"
            className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors">
            <Trash2 size={15} />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 mt-2 text-xs text-slate-400 flex-wrap">
        {isCurrent && <span className="text-emerald-600 font-medium">Open now</span>}
        <span>Last opened {fmtUK(win.last_opened_at)}</span>
        <span>Last closed {fmtUK(win.last_closed_at)}</span>
        <span>{win.timezone}</span>
      </div>
    </div>
  );
}


function WindowsPanel({ status, onRefresh }) {
  const [adding, setAdding] = useState(false);
  const [busy, setBusy]     = useState(false);

  const windows = status?.windows || [];
  const current = status?.current_window;

  const save = async (id, payload) => {
    try {
      await inferenceAPI.updateWindow(id, payload);
      toast.success('Window updated');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the window');
    }
  };

  const remove = async (win) => {
    if (!window.confirm(`Remove the "${win.name}" window?`)) return;
    try {
      await inferenceAPI.deleteWindow(win.id);
      toast.success('Window removed');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove');
    }
  };

  const openNow = async (id) => {
    setBusy(true);
    try {
      const { data } = await inferenceAPI.openWindow(id);
      toast.success(data.endpoint_ready ? 'Window open and endpoint answering' : 'Window open — endpoint still starting');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not open the window');
    } finally {
      setBusy(false);
    }
  };

  const closeNow = async () => {
    setBusy(true);
    try {
      const { data } = await inferenceAPI.closeWindow();
      toast.success(`Window closed. ${data.remaining || 0} job(s) stay queued.`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not close the window');
    } finally {
      setBusy(false);
    }
  };

  const addWindow = async () => {
    setAdding(true);
    try {
      await inferenceAPI.createWindow({
        name: 'New window',
        start_time_local: '12:00',
        max_duration_minutes: 180,
        idle_shutdown_minutes: 15,
        is_active: false,
      });
      toast.success('Window added — set the time and switch it on');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add the window');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="max-w-2xl">
          <p className="text-sm text-slate-500">
            Generation runs in bursts. Each window starts the GPU, works the queue, and stops once the queue
            has been idle for the set period or the maximum duration is reached. Anything still queued waits
            for the next window.
          </p>
          <div className="mt-2 inline-flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-1.5">
            <UkClock />
          </div>
          <p className="text-xs text-slate-400 mt-1.5">
            Window times are UK time. Set them against the clock above, not your own.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {current && (
            <button onClick={closeNow} disabled={busy}
              className="flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
              <StopCircle size={14} /> Close window
            </button>
          )}
          <button onClick={addWindow} disabled={adding}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
            {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add window
          </button>
        </div>
      </div>

      {windows.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm">
          No windows yet. Add one to schedule when generation runs.
        </div>
      ) : (
        <div className="space-y-2">
          {windows.map(w => (
            <WindowRow key={w.id} window={w}
              isCurrent={current?.name === w.name}
              onSave={save} onDelete={remove} onOpen={openNow} />
          ))}
        </div>
      )}
    </div>
  );
}


const PRIORITY_LABELS = {
  0:  'Low priority',
  5:  'Medium priority',
  10: 'High priority',
};

const JOB_STATUS_STYLES = {
  pending:    'bg-slate-100 text-slate-600 border-slate-200',
  processing: 'bg-blue-50 text-blue-700 border-blue-200',
  done:       'bg-emerald-50 text-emerald-700 border-emerald-200',
  failed:     'bg-red-50 text-red-600 border-red-200',
  cancelled:  'bg-slate-100 text-slate-400 border-slate-200',
};

const QUEUE_TABS = [
  { id: '',           label: 'All' },
  { id: 'pending',    label: 'Pending' },
  { id: 'processing', label: 'Processing' },
  { id: 'done',       label: 'Done' },
  { id: 'failed',     label: 'Failed' },
  { id: 'cancelled',  label: 'Cancelled' },
];

const PAGE_SIZE = 100;

const liveUrl = (job) => {
  if (!job.publication_domain || !job.story_slug) return null;
  const tag = job.story_tags?.[0]
    ? String(job.story_tags[0]).toLowerCase().replace(/\s+/g, '-')
    : 'news';
  return `https://${job.publication_domain}/${tag}/${job.story_slug}`;
};


function QueuePanel() {
  const [status, setStatus]   = useState(null);
  const [jobs, setJobs]       = useState([]);
  const [counts, setCounts]   = useState({});
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(null);
  const [filter, setFilter]   = useState('');
  const [offset, setOffset]   = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, j] = await Promise.all([
        automationAPI.getStatus(),
        automationAPI.getJobs({
          limit: PAGE_SIZE,
          offset,
          ...(filter ? { status: filter } : {}),
        }),
      ]);
      setStatus(s.data);
      setJobs(j.data.jobs || []);
      setCounts(j.data.counts || {});
      setTotal(j.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load the queue');
    } finally {
      setLoading(false);
    }
  }, [filter, offset]);

  useEffect(() => { load(); }, [load]);

  const changeFilter = (id) => {
    setOffset(0);
    setFilter(id);
  };

  const queuePending = async () => {
    setBusy('queue');
    try {
      const { data } = await automationAPI.queuePending({ limit: 100, autopilot_only: true });
      toast.success(`Queued ${data.queued || 0} job(s) from ${data.items || 0} item(s)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not queue');
    } finally {
      setBusy(null);
    }
  };

  const drain = async () => {
    setBusy('drain');
    try {
      const { data } = await automationAPI.drain({ max_jobs: 10 });
      if (!data.gpu_available) {
        toast.error(`Endpoint offline — ${data.pending || 0} job(s) waiting: ${data.detail}`);
      } else {
        toast.success(`Generated ${data.done || 0}, failed ${data.failed || 0}, deferred ${data.deferred || 0}`);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Drain failed');
    } finally {
      setBusy(null);
    }
  };

  const jobAction = async (job, action) => {
    try {
      if (action === 'retry') await automationAPI.retryJob(job.id);
      else await automationAPI.cancelJob(job.id);
      toast.success(action === 'retry' ? 'Job requeued' : 'Job cancelled');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const queue = status?.queue || {};
  const page  = Math.floor(offset / PAGE_SIZE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat label="Due now"    value={queue.due_now ?? 0} tone={queue.due_now > 0 ? 'warn' : undefined} />
          <Stat label="Pending"    value={queue.pending ?? 0} />
          <Stat label="Deferred"   value={queue.deferred ?? 0} />
          <Stat label="Processing" value={queue.processing ?? 0} tone={queue.processing > 0 ? 'good' : undefined} />
          <Stat label="Next due"   value={queue.next_due_at ? fmtUK(queue.next_due_at) : '—'} />
        </div>

        <div className="flex items-center gap-2 flex-wrap mt-4 pt-4 border-t border-slate-100">
          <button onClick={queuePending} disabled={busy !== null}
            className="flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
            {busy === 'queue' ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Queue pending items
          </button>
          <button onClick={drain} disabled={busy !== null}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
            {busy === 'drain' ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />}
            Run 10 jobs now
          </button>
          <button onClick={load}
            className="h-9 w-9 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors">
            <RefreshCw size={15} />
          </button>
        </div>
      </div>

      <div className="flex gap-1 flex-wrap border-b border-slate-200">
        {QUEUE_TABS.map(t => (
          <button key={t.id || 'all'} onClick={() => changeFilter(t.id)}
            className={`px-2.5 py-2 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${
              filter === t.id
                ? 'border-blue-600 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-900 hover:border-slate-300'
            }`}>
            {t.label}
            <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[20px] text-center ${
              filter === t.id ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-500'
            }`}>
              {counts[t.id || 'all'] ?? 0}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
          <Loader2 size={16} className="animate-spin" /> Loading queue...
        </div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm">
          Nothing here. Queue pending items to give the next window something to do.
        </div>
      ) : (
        <>
          <p className="text-xs text-slate-400">
            Showing {offset + 1}–{Math.min(offset + jobs.length, total)} of {total} · times in UK
          </p>

          <div className="space-y-2">
            {jobs.map(j => {
              const url = liveUrl(j);
              return (
                <div key={j.id} className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
                  <div className="flex items-start gap-3">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border font-semibold shrink-0 ${
                      JOB_STATUS_STYLES[j.status] || JOB_STATUS_STYLES.pending
                    }`}>
                      {j.status}
                    </span>

                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-900 font-medium leading-snug truncate">
                        {j.story_headline || j.item_title || '(item no longer available)'}
                      </p>

                      <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-400 flex-wrap">
                        {j.source_name && (
                          <a href={`/feeds/sources/${j.feed_source_id}`}
                            className="text-slate-500 hover:text-blue-600 transition-colors">
                            {j.source_name}
                          </a>
                        )}
                        {j.publication_name && (
                          <>
                            <span className="text-slate-300">→</span>
                            <span className="text-slate-500">{j.publication_name}</span>
                          </>
                        )}
                        <span className="text-slate-300">·</span>
                        <span className="font-mono">{j.model_id || '—'}</span>
                        {j.duration_seconds != null && (
                          <>
                            <span className="text-slate-300">·</span>
                            <span title="Total job time, from claim to finish">
                              {j.duration_seconds}s total
                            </span>
                          </>
                        )}
                        {j.model_ms ? (
                          <>
                            <span className="text-slate-300">·</span>
                            <span title="Model inference time only — the rest is fetching, validation and database work">
                              {(j.model_ms / 1000).toFixed(1)}s model
                            </span>
                          </>
                        ) : null}
                      </div>

                      <div className="flex items-center gap-3 mt-1.5 text-[11px] flex-wrap">
                        {j.feed_item_id && (
                          <a href={`/feeds/items/${j.feed_item_id}`}
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:underline font-medium">
                            Feed item
                          </a>
                        )}
                        {j.item_url && (
                          <a href={j.item_url} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-slate-500 hover:text-blue-600 hover:underline font-medium">
                            Source <ExternalLink size={10} />
                          </a>
                        )}
                        {url && j.story_status === 'published' && (
                          <a href={url} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700 hover:underline font-medium">
                            View live <ExternalLink size={10} />
                          </a>
                        )}
                      </div>

                      <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-400 flex-wrap">
                        <span className={`uppercase tracking-wider font-semibold ${
                          j.triggered_by === 'manual' ? 'text-slate-500' : 'text-blue-600'
                        }`}>
                          {j.triggered_by === 'manual' ? 'Manual' : 'Autopilot'}
                        </span>
                        <span className="text-slate-300">·</span>
                        <span>{PRIORITY_LABELS[j.priority] || `priority ${j.priority}`}</span>
                        {j.attempts > 0 && (
                          <>
                            <span className="text-slate-300">·</span>
                            <span>attempt {j.attempts}/{j.max_attempts}</span>
                          </>
                        )}
                        <span className="text-slate-300">·</span>
                        <span>
                          {j.status === 'done'
                            ? `finished ${fmtUK(j.completed_at)}`
                            : `due ${fmtUK(j.next_attempt_at)}`}
                        </span>
                      </div>

                      {(j.error_message || j.defer_reason) && (
                        <p className={`text-xs mt-1.5 ${j.defer_reason ? 'text-amber-600' : 'text-red-500'}`}>
                          {j.defer_reason || j.error_message}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      {['failed', 'cancelled'].includes(j.status) && (
                        <button onClick={() => jobAction(j, 'retry')} title="Requeue"
                          className="h-7 w-7 inline-flex items-center justify-center rounded text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                          <RotateCcw size={13} />
                        </button>
                      )}
                      {['pending', 'processing'].includes(j.status) && (
                        <button onClick={() => jobAction(j, 'cancel')} title="Cancel"
                          className="h-7 w-7 inline-flex items-center justify-center rounded text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                          <XCircle size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {total > PAGE_SIZE && (
            <div className="flex items-center justify-center gap-3 pt-2">
              <button
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0}
                className="px-3 py-1.5 bg-white hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600 text-sm rounded border border-slate-200"
              >
                Previous
              </button>
              <span className="text-xs text-slate-400">Page {page} of {pages}</span>
              <button
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={offset + PAGE_SIZE >= total}
                className="px-3 py-1.5 bg-white hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600 text-sm rounded border border-slate-200"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ─── Settings ───

function SettingsPanel({ onRefresh }) {
  const [form, setForm]       = useState(null);
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [ticking, setTicking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await automationAPI.getSettings();
      setForm(data);
      setOriginal(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const dirty = form && original && JSON.stringify(form) !== JSON.stringify(original);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await automationAPI.updateSettings({
        fetch_autopilot_enabled:      form.fetch_autopilot_enabled,
        generation_autopilot_enabled: form.generation_autopilot_enabled,
        gpu_control_enabled:          form.gpu_control_enabled,
        fetch_tick_seconds:           parseInt(form.fetch_tick_seconds, 10),
        fetch_concurrency:            parseInt(form.fetch_concurrency, 10),
        extract_concurrency:          parseInt(form.extract_concurrency, 10),
        max_items_per_fetch:          parseInt(form.max_items_per_fetch, 10),
        rewrite_concurrency:          parseInt(form.rewrite_concurrency, 10),
        image_concurrency:            parseInt(form.image_concurrency, 10),
        default_max_stories_per_day:  parseInt(form.default_max_stories_per_day, 10),
        max_images_per_day:           parseInt(form.max_images_per_day, 10),
      });
      setForm(data);
      setOriginal(data);
      toast.success('Settings saved');
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const runTick = async () => {
    setTicking(true);
    try {
      const { data } = await inferenceAPI.runTick();
      if (data.error) toast.error(data.error);
      else toast.success(
        `Fetched ${data.fetch?.sources ?? 0}, queued ${data.enqueue?.queued ?? 0}, generated ${data.drain?.done ?? 0}`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Tick failed');
    } finally {
      setTicking(false);
    }
  };

  if (loading || !form) {
    return (
      <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
        <Loader2 size={16} className="animate-spin" /> Loading settings...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
        <h2 className="text-sm font-semibold text-slate-900">Autopilot</h2>
        <p className="text-xs text-slate-400 -mt-2">
          Global switches. Each source also has its own autopilot toggle, and both must be on
          for that source to run automatically.
        </p>

        <Toggle
          checked={form.fetch_autopilot_enabled}
          onChange={v => update('fetch_autopilot_enabled', v)}
          label="Fetch sources automatically"
          hint="Costs bandwidth only. Runs continuously, independent of the GPU."
        />
        <Toggle
          checked={form.generation_autopilot_enabled}
          onChange={v => update('generation_autopilot_enabled', v)}
          label="Generate stories automatically"
          hint="Queues new items and works the queue whenever the endpoint is reachable."
        />
        <Toggle
          checked={form.gpu_control_enabled}
          onChange={v => update('gpu_control_enabled', v)}
          label="Start and stop the GPU on a schedule"
          hint="Restricts generation to the windows above. Leave off while starting the box by hand."
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-5">
        <h2 className="text-sm font-semibold text-slate-900">Limits</h2>
        <p className="text-xs text-slate-400 -mt-2">
          Each source is fetched at its own configured interval. A source's priority decides
          which one goes first when several are due at once, and which stories are generated
          first when the queue is deep.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label="Tick interval (s)">
            <input type="number" min="30" value={form.fetch_tick_seconds}
              onChange={e => update('fetch_tick_seconds', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Fetches at once">
            <input type="number" min="1" value={form.fetch_concurrency}
              onChange={e => update('fetch_concurrency', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Extractions at once">
            <input type="number" min="1" value={form.extract_concurrency}
              onChange={e => update('extract_concurrency', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Items per fetch">
            <input type="number" min="1" value={form.max_items_per_fetch}
              onChange={e => update('max_items_per_fetch', e.target.value)} className={inputCls} />
          </Field>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label="Generations at once"
            hint="Must not exceed OLLAMA_NUM_PARALLEL on the GPU box.">
            <input type="number" min="1" value={form.rewrite_concurrency}
              onChange={e => update('rewrite_concurrency', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Images at once">
            <input type="number" min="1" value={form.image_concurrency}
              onChange={e => update('image_concurrency', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Stories per site per day"
            hint="Used when a subscription has no cap of its own.">
            <input type="number" min="1" value={form.default_max_stories_per_day}
              onChange={e => update('default_max_stories_per_day', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Images per day">
            <input type="number" min="1" value={form.max_images_per_day}
              onChange={e => update('max_images_per_day', e.target.value)} className={inputCls} />
          </Field>
        </div>


        <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
          <button onClick={save} disabled={saving || !dirty}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
            {saving && <Loader2 size={14} className="animate-spin" />} Save settings
          </button>
          {dirty && (
            <button onClick={() => setForm(original)}
              className="px-4 py-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">
              Discard changes
            </button>
          )}
          <button onClick={runTick} disabled={ticking}
            className="ml-auto flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
            {ticking ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />}
            Run one pass now
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── Main ───

export default function ComputeTab({ view }) {
  const [status, setStatus]   = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await inferenceAPI.getGpuStatus();
      setStatus(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load compute status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === 'queue') { setLoading(false); return; }
    load();
  }, [load, view]);

  if (view === 'queue')    return <QueuePanel />;
  if (view === 'settings') return <SettingsPanel onRefresh={load} />;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
        <Loader2 size={16} className="animate-spin" /> Loading compute...
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {status?.scheduler && !status.scheduler.running && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
          <AlertTriangle size={15} className="shrink-0 mt-0.5" />
          <span>The scheduler is not running, so nothing happens automatically. Restart the backend, or start it from the API.</span>
        </div>
      )}

      <InstancePanel status={status} onRefresh={load} />
      <WindowsPanel status={status} onRefresh={load} />
    </div>
  );
}